import { ComponentType, useCallback, useMemo, useState } from 'react'
import { Button } from 'shared/ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'shared/ui/tooltip'

export interface NavigationItem {
  id: string
  label: string
  icon: ComponentType<{ className?: string }>
  path: string
  roleId?: string
  roles?: string[]
  count?: number
  children?: NavigationItem[]
}

interface NavigationMenuProps {
  items: NavigationItem[]
  currentPath: string
  onNavigate: (path: string) => void
  currentUser: any
  isCollapsed?: boolean
}

export function NavigationMenu({
  items,
  currentPath,
  onNavigate,
  currentUser,
  isCollapsed = false,
}: NavigationMenuProps) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())

  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  const roleText =
    typeof currentUser?.roleLabel === 'string'
      ? currentUser.roleLabel
      : typeof currentUser?.role === 'string'
        ? currentUser.role
        : undefined
  const normalizedUserRoles = useMemo(
    () =>
      roleText
        ? roleText
            .split(',')
            .map((r: string) => r.trim().toLowerCase())
            .filter(Boolean)
        : [],
    [roleText],
  )

  const filterByRole = useCallback((item: NavigationItem): boolean => {
    if (!item.roles || item.roles.length === 0 || normalizedUserRoles.length === 0) return true
    return item.roles.some((role) => normalizedUserRoles.includes(role.trim().toLowerCase()))
  }, [normalizedUserRoles])

  const visibleItems = useMemo(() => items.filter(filterByRole), [filterByRole, items])

  const renderItems = (nodes: NavigationItem[], depth = 0) =>
    nodes.map((item) => {
      const Icon = item.icon
      const hasChildren = !!item.children && item.children.length > 0
      const isActive =
        currentPath === item.path ||
        (Boolean(item.path) && item.path !== '/' && currentPath.startsWith(`${item.path}/`))
      const childMatch = hasChildren
        ? (item.children ?? []).some((c) => (c.path ? currentPath.startsWith(c.path) : false))
        : false
      const isExpanded = expandedIds.has(String(item.id)) || childMatch

      const button = (
        <Button
          key={item.id}
          variant={isActive ? 'default' : 'ghost'}
          className={`w-full ${isCollapsed ? 'h-9 justify-center px-2' : 'h-auto min-h-9 justify-start gap-2 px-2 py-2'}`}
          style={!isCollapsed && depth > 0 ? { paddingLeft: 10 + depth * 6 } : undefined}
          onClick={() => {
            if (hasChildren) {
              toggleExpand(String(item.id))
              if (!item.path) return
            }
            onNavigate(item.path)
          }}
        >
          <Icon className="h-4 w-4" />
          {!isCollapsed ? (
            <div className="flex w-full items-center justify-between">
              <span className="flex min-w-0 items-start gap-2">
                <span className="whitespace-normal break-words text-left leading-5">{item.label}</span>
                {typeof item.count === 'number' && item.count > 0 ? (
                  <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-emerald-100 px-2 text-[11px] font-semibold text-emerald-700 ring-1 ring-emerald-200">
                    {item.count > 99 ? '99+' : item.count}
                  </span>
                ) : null}
              </span>
              {hasChildren ? <span className="text-xs text-gray-400">{isExpanded ? '▾' : '▸'}</span> : null}
            </div>
          ) : null}
        </Button>
      )

      const buttonWithTooltip = isCollapsed ? (
        <Tooltip key={item.id}>
          <TooltipTrigger asChild>{button}</TooltipTrigger>
          <TooltipContent side="right">
            <p>{item.label}</p>
          </TooltipContent>
        </Tooltip>
      ) : (
        button
      )

      return (
        <div key={item.id} className="space-y-1">
          {buttonWithTooltip}
          {!isCollapsed && hasChildren && isExpanded ? (
            <div className="ml-2 border-l border-gray-100 pl-2">
              {renderItems((item.children ?? []).filter(filterByRole), depth + 1)}
            </div>
          ) : null}
        </div>
      )
    })

  return (
    <TooltipProvider>
      <div className="transition-all duration-300">
        <nav className="space-y-1">{renderItems(visibleItems)}</nav>
      </div>
    </TooltipProvider>
  )
}
